import React, { useState, useEffect } from "react";
import {
    Box,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Checkbox,
    Typography,
    CircularProgress,
} from "@mui/material";
import Axios from "axios";
import { getAuthHeader } from "../../utils/authToken";

const studentName = (s) => `${s.first_name || ""} ${s.last_name || ""}`.trim() || s.email || `#${s.id}`;

// Selective-assignment control: "All enrolled" vs "Selected students" (multi-select
// from the program roster). `rosterParams` scopes the roster fetch — e.g.
// { program_course_id } for a subject course's round, or { role: "PRACTICE" } for exams.
// value = { assign_mode, student_ids }; onChange receives the next value object.
const AssignToField = ({ programUuid, rosterParams, value, onChange }) => {
    const base = `${process.env.REACT_APP_URL}/api/v1/prep-programs/${programUuid}`;
    const [roster, setRoster] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let active = true;
        setLoading(true);
        Axios.get(`${base}/students`, { headers: { Authorization: getAuthHeader() }, params: rosterParams })
            .then((res) => active && setRoster(res.data || []))
            .catch(() => active && setRoster([]))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programUuid, JSON.stringify(rosterParams)]);

    const mode = value?.assign_mode === "SELECTED" ? "SELECTED" : "ALL";
    const ids = value?.student_ids || [];

    return (
        <Box>
            <RadioGroup
                row
                value={mode}
                onChange={(e) => onChange({ assign_mode: e.target.value, student_ids: e.target.value === "ALL" ? [] : ids })}
            >
                <FormControlLabel value="ALL" control={<Radio size="small" />} label="All enrolled students" />
                <FormControlLabel value="SELECTED" control={<Radio size="small" />} label="Selected students" />
            </RadioGroup>
            {mode === "SELECTED" &&
                (loading ? (
                    <CircularProgress size={18} />
                ) : roster.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                        No enrolled students found for this course.
                    </Typography>
                ) : (
                    <FormControl size="small" fullWidth sx={{ mt: 0.5 }}>
                        <InputLabel>Students</InputLabel>
                        <Select
                            multiple
                            value={ids}
                            onChange={(e) => onChange({ assign_mode: "SELECTED", student_ids: e.target.value })}
                            input={<OutlinedInput label="Students" />}
                            renderValue={(sel) => roster.filter((s) => sel.includes(s.id)).map(studentName).join(", ")}
                        >
                            {roster.map((s) => (
                                <MenuItem key={s.id} value={s.id}>
                                    <Checkbox size="small" checked={ids.includes(s.id)} />
                                    {studentName(s)}
                                    {s.email ? ` · ${s.email}` : ""}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ))}
        </Box>
    );
};

export default AssignToField;
